import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import { mapAclRolesResponseToDto } from '../dto/system-acl.dto.js';
import { buildSystemAclEditPath, buildSystemAclNewPath } from '../model/system-acl-routes.js';

export function useSystemAcl() {
  const { token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const rolesResponse = await adminApi.getAclRoles(token);
      setRoles(mapAclRolesResponseToDto(rolesResponse));
    } catch (requestError) {
      setRoles([]);
      setError(requestError?.message || 'Failed to load access control data');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!location.state?.success) {
      return;
    }

    setSuccess(String(location.state.success));
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const openCreateRole = useCallback(() => {
    setError('');
    setSuccess('');
    navigate(buildSystemAclNewPath());
  }, [navigate]);

  const openEditRole = useCallback((role) => {
    setError('');
    setSuccess('');
    navigate(buildSystemAclEditPath(role?.id));
  }, [navigate]);

  const confirmDeleteRole = useCallback((role) => {
    setDeleteTarget(role);
    setError('');
    setSuccess('');
  }, []);

  const cancelDeleteRole = useCallback(() => {
    if (isSaving) {
      return;
    }

    setDeleteTarget(null);
  }, [isSaving]);

  const deleteRole = useCallback(async () => {
    if (!deleteTarget) {
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      await adminApi.deleteAclRole(token, deleteTarget.id);
      setRoles((current) => current.filter((role) => role.id !== deleteTarget.id));
      setDeleteTarget(null);
      setSuccess('Role deleted.');
    } catch (requestError) {
      setError(requestError?.message || 'Failed to delete role');
    } finally {
      setIsSaving(false);
    }
  }, [deleteTarget, token]);

  return {
    roles,
    isLoading,
    isSaving,
    error,
    success,
    deleteTarget,
    openCreateRole,
    openEditRole,
    confirmDeleteRole,
    cancelDeleteRole,
    deleteRole,
    refresh: loadData,
  };
}
